/**
 * HostTournamentPanel
 *
 * Staff-only "Host" tab body in the Fun Challenges lobby. Visual language
 * mirrors the Multiplayer "Host a race" create card (Card + settings-summary
 * chips + "Tap to edit ›" + PrimaryButton), so the Host tab feels native to
 * the rest of Fun Challenges.
 *
 *   1. Create card — start time + max players inline; topics / time / difficulty
 *      / punish behind a "Tap to edit" SlideModal (shared RoyaleSettings). A
 *      "Schedule tournament" button commits via fun_tournament_create.
 *   2. Scheduled list — ALL staff-hosted scheduled/live tournaments (D4/D12),
 *      each with Start-now + Cancel (confirm-gated).
 *
 * Lifecycle writes are socket messages (game-service owns timer/CAS/room);
 * reads are the REST list hook. Staff-gating is done by the caller.
 *
 * Datetime: the "Start time" input collects the host's LOCAL time (browser tz,
 * labelled) and is converted to a UTC ISO string on the wire. Topics are
 * OPTIONAL (empty => any topic), matching the multiplayer royale create.
 */

import React, {useEffect, useMemo, useState} from 'react';
import {Platform, Pressable, ScrollView, StyleSheet, View} from 'react-native';
import MaterialCommunityIcons from 'react-native-vector-icons/MaterialCommunityIcons';

import Box from '@/Atoms/Box';
import CCText from '@/Atoms/CCText';
import CCButton from '@/Atoms/CCButton';
import CCColors from '@/Utils/CCColors';
import SlideModal from '@/Atoms/SlideModal';
import {FontSizes, vs} from '@/Utils/Dimensions';

import CCDateTimePicker from '@/Atoms/CCDateTimePicker';
import RoyaleSettings from '@/Screens/FunChallenges/components/RoyaleSettings';
import {DEFAULT_ROYALE_SETTINGS} from '@/Screens/FunChallenges/royaleConstants';
import type {RoyaleSettings as RoyaleSettingsValue} from '@/Screens/FunChallenges/royaleTypes';
import useRoyaleSocket from '@/Hooks/FunChallengesSocket/useRoyaleSocket';
import useFunChallengesTournamentStore from '@/Store/funChallengesTournamentStore';
import {
  useRoyaleTournaments,
  type RoyaleTournamentSummary,
} from '@/Hooks/FunChallengesApi/useRoyaleTournaments';
import {
  buildTournamentLink,
  copyToClipboard,
  tournamentDisplayName,
} from '@/Screens/FunChallenges/util/tournamentLink';

const TEAL = '#3DAB9E';
const TEAL_DARK = '#1F6C63';
const TEAL_TINT_SOFT = 'rgba(61, 171, 158, 0.06)';
const BROWN = '#4D3F37';
const TEXT_DARK = '#262322';
const BORDER = 'rgba(38, 35, 34, 0.1)';
const MAX_PLAYER_CHIPS = [10, 25, 50, 100];

interface Props {
  creatorName: string;
}

function localTzLabel(): string {
  try {
    const parts = new Intl.DateTimeFormat(undefined, {
      timeZoneName: 'short',
    }).formatToParts(new Date());
    const tz = parts.find(p => p.type === 'timeZoneName');
    return tz ? tz.value : 'local';
  } catch (e) {
    return 'local';
  }
}

function toLocalInputValue(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}

/** Parse a `YYYY-MM-DDTHH:mm` local-input string back into a local Date
 *  (Date.parse on that shape is treated as local time). Falls back to now. */
function scheduleLocalToDate(v: string): Date {
  const ms = Date.parse(v);
  return isNaN(ms) ? new Date() : new Date(ms);
}

function defaultScheduleLocal(): string {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(11, 0, 0, 0); // tomorrow 11:00 local (product default 11:00 IST)
  return toLocalInputValue(d);
}

/**
 * What the Share button copies.
 *
 * P8: the ONE durable, state-aware deep link (`puzzle-royale-tournament/:id`).
 * The numeric id resolves at any lifecycle stage, so a single link serves
 * register (scheduled) → join/watch (live) → results (completed). This
 * replaces the old "copy the live share_code" stopgap and NEVER points at the
 * chess `/CCTournaments?tournamentId=` route (different id space — pitfall 34).
 * Shown for every state except cancelled (nothing to open there).
 */
function tournamentShareText(t: RoyaleTournamentSummary): string | null {
  if (t.state === 'cancelled') return null;
  return buildTournamentLink(t.id);
}

function Card({title, children}: {title?: string; children: React.ReactNode}) {
  return (
    <Box style={styles.card}>
      {title ? <CCText style={styles.cardTitle}>{title}</CCText> : null}
      {children}
    </Box>
  );
}

function Chip({
  text,
  icon,
  raw,
  active,
  onPress,
}: {
  text: string;
  icon?: string;
  raw?: boolean;
  active?: boolean;
  onPress?: () => void;
}) {
  const body = (
    <View style={[styles.chip, active && styles.chipActive]}>
      {icon ? (
        <MaterialCommunityIcons
          name={icon}
          size={13}
          color={active ? CCColors.White : TEAL_DARK}
          style={{marginTop: 0.5}}
        />
      ) : null}
      <CCText
        style={[
          styles.chipText,
          raw && styles.chipTextRaw,
          active && styles.chipTextActive,
        ]}>
        {text}
      </CCText>
    </View>
  );
  return onPress ? <Pressable onPress={onPress}>{body}</Pressable> : body;
}

function PrimaryButton({
  label,
  disabled,
  onPress,
}: {
  label: string;
  disabled?: boolean;
  onPress: () => void;
}) {
  return (
    <Pressable
      onPress={disabled ? undefined : onPress}
      disabled={disabled}
      style={({pressed}) => [
        styles.primaryBtn,
        disabled && styles.primaryBtnDisabled,
        pressed && !disabled && {opacity: 0.9},
      ]}>
      <CCText style={styles.primaryBtnText}>{label}</CCText>
    </Pressable>
  );
}

export default function HostTournamentPanel({creatorName}: Props) {
  const {createTournament, startTournamentNow, cancelTournament} =
    useRoyaleSocket();
  const lastCreatedId = useFunChallengesTournamentStore(s => s.lastCreatedId);

  const [scheduleLocal, setScheduleLocal] = useState<string>(
    defaultScheduleLocal(),
  );
  const [name, setName] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [isPrivate, setIsPrivate] = useState<boolean>(false);
  const [password, setPassword] = useState<string>('');
  const [customPuzzles, setCustomPuzzles] = useState<boolean>(false);
  const [maxPlayers, setMaxPlayers] = useState<number>(100);
  const [settings, setSettings] = useState<RoyaleSettingsValue>(
    DEFAULT_ROYALE_SETTINGS,
  );
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [confirm, setConfirm] = useState<{
    kind: 'start' | 'cancel';
    id: number;
  } | null>(null);
  const [copiedId, setCopiedId] = useState<number | null>(null);

  const tz = useMemo(() => localTzLabel(), []);

  const onShare = (t: RoyaleTournamentSummary) => {
    const text = tournamentShareText(t);
    if (!text) return;
    if (copyToClipboard(text)) {
      setCopiedId(t.id);
      setTimeout(() => setCopiedId(c => (c === t.id ? null : c)), 1500);
    }
  };

  const {data: tournaments, refetch} = useRoyaleTournaments(
    ['scheduled', 'live'],
    {refetchIntervalMs: 10_000},
  );

  // When the server confirms creation (fun_tournament_created), stop the
  // spinner and refresh the list so the new row appears immediately.
  useEffect(() => {
    if (submitting && lastCreatedId) {
      setSubmitting(false);
      refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastCreatedId]);

  const canCreate = useMemo(
    () => !submitting && !isNaN(Date.parse(scheduleLocal)),
    [scheduleLocal, submitting],
  );

  const topicCount = (settings.topics || []).length;

  const onCreate = () => {
    const localMs = Date.parse(scheduleLocal);
    if (isNaN(localMs)) return;
    setSubmitting(true);
    createTournament({
      scheduledAt: new Date(localMs).toISOString(),
      creatorName,
      settings: {
        maxPlayers,
        timerSec: settings.timerSec,
        topics: settings.topics || [],
        difficulty: settings.difficulty,
        customPuzzles,
        name: name.trim(),
        description: description.trim(),
        isPrivate,
        password: isPrivate ? password : '',
      },
    });
    // Fallback if the server echo is missed — clear the spinner + refresh.
    setTimeout(() => {
      setSubmitting(false);
      refetch();
    }, 2500);
  };

  const doConfirm = () => {
    if (!confirm) return;
    if (confirm.kind === 'start') startTournamentNow(String(confirm.id));
    else cancelTournament(String(confirm.id));
    setConfirm(null);
    setTimeout(() => refetch(), 800);
  };

  return (
    <ScrollView style={styles.root} contentContainerStyle={styles.content}>
      {/* ── Create card ─────────────────────────────────────────────────── */}
      <Card title="Schedule a tournament">
        {/* Tournament name (optional) */}
        <CCText style={styles.fieldLabel}>Tournament name (optional)</CCText>
        {Platform.OS === 'web' ? (
          // @ts-ignore RN-web forwards DOM props to the host input.
          <input
            type="text"
            value={name}
            maxLength={120}
            placeholder="e.g. Friday Night Royale"
            onChange={(e: any) => setName(e.target.value)}
            style={webInputStyle as any}
          />
        ) : null}

        <CCText style={styles.fieldLabel}>{`Start time (${tz})`}</CCText>
        {/* Native datetime field — the host can either TYPE the value or open
            the browser's calendar/time popup. Styled to match OTB's date field.
            (The old one-tap presets were removed at the user's request.) */}
        <CCDateTimePicker
          value={scheduleLocalToDate(scheduleLocal)}
          minDate={new Date()}
          tzLabel={tz}
          onChange={d => setScheduleLocal(toLocalInputValue(d))}
        />

        <CCText style={styles.fieldLabel}>Max players</CCText>
        <View style={styles.chipRow}>
          {MAX_PLAYER_CHIPS.map(n => (
            <Chip
              key={n}
              text={String(n)}
              raw
              active={maxPlayers === n}
              onPress={() => setMaxPlayers(n)}
            />
          ))}
        </View>

        {/* Settings summary → SlideModal (mirrors "Host a race" card) */}
        <Pressable
          onPress={() => setSettingsOpen(true)}
          style={({pressed}) => [
            styles.settingsCard,
            pressed && styles.settingsCardPressed,
          ]}>
          <View style={styles.settingsChips}>
            <Chip
              icon="tag-multiple"
              text={topicCount === 0 ? 'Any topic' : `${topicCount} topics`}
            />
            <Chip icon="timer-outline" text={`${settings.timerSec}s`} raw />
            <Chip icon="speedometer" text={settings.difficulty} />
            {/* Punish chip hidden alongside the control itself (see the
                allowPunish={false} note on RoyaleSettings below) — it displayed a
                setting the tournament never actually applied.
            <Chip
              icon="alert-octagon-outline"
              text={
                settings.punish
                  ? `Punish ${settings.punishThreshold ?? 5}`
                  : 'No punish'
              }
            /> */}
          </View>
          <CCText style={styles.settingsHint}>Tap to edit ›</CCText>
        </Pressable>

        {/* Description (optional) */}
        <CCText style={styles.fieldLabel}>Description (optional)</CCText>
        {Platform.OS === 'web' ? (
          // @ts-ignore RN-web forwards DOM props to the host textarea.
          <textarea
            value={description}
            maxLength={1000}
            placeholder="Shown on the tournament's Details tab"
            onChange={(e: any) => setDescription(e.target.value)}
            style={{...(webInputStyle as any), minHeight: 60, resize: 'vertical'}}
          />
        ) : null}

        {/* ── HIDDEN FOR NOW (user request) ────────────────────────────────
            "Private tournament" (+ password) and "Custom puzzles" are both
            STORED-BUT-NOT-ENFORCED stubs: the columns exist and the values ride
            the create payload, but nothing reads them yet. Showing toggles that
            do nothing is misleading, so the controls are commented out until the
            behaviour is actually built. The `isPrivate` / `password` /
            `customPuzzles` state is deliberately LEFT IN PLACE (defaulting to
            false/'') so the wire payload and the backend contract are unchanged
            — un-commenting these two blocks is all that's needed to restore the
            UI once enforcement lands.

        <Pressable
          onPress={() => setIsPrivate(v => !v)}
          style={styles.privateRow}>
          <MaterialCommunityIcons
            name={isPrivate ? 'checkbox-marked' : 'checkbox-blank-outline'}
            size={20}
            color={isPrivate ? TEAL : CCColors.TextColor.GreyText}
          />
          <View style={{flex: 1}}>
            <CCText fontSize={FontSizes[13]} color={TEXT_DARK}>
              Private tournament
            </CCText>
            <CCText fontSize={FontSizes[11]} color={CCColors.TextColor.GreyText}>
              Coming soon — hidden from discovery, entry by link/password.
              Saved now so it's ready.
            </CCText>
          </View>
        </Pressable>
        {isPrivate && Platform.OS === 'web' ? (
          <input
            type="text"
            value={password}
            maxLength={128}
            placeholder="Entry password (optional)"
            onChange={(e: any) => setPassword(e.target.value)}
            style={webInputStyle as any}
          />
        ) : null}

        <Pressable
          onPress={() => setCustomPuzzles(v => !v)}
          style={styles.privateRow}>
          <MaterialCommunityIcons
            name={customPuzzles ? 'checkbox-marked' : 'checkbox-blank-outline'}
            size={20}
            color={customPuzzles ? TEAL : CCColors.TextColor.GreyText}
          />
          <View style={{flex: 1}}>
            <CCText fontSize={FontSizes[13]} color={TEXT_DARK}>
              Custom puzzles
            </CCText>
            <CCText fontSize={FontSizes[11]} color={CCColors.TextColor.GreyText}>
              Coming soon — use your own puzzle set instead of the difficulty
              band. Saved now so it's ready.
            </CCText>
          </View>
        </Pressable>
            ──────────────────────────────────────────────────────────────── */}

        <CCText style={styles.createNote}>
          Players register from the tournaments page. It auto-starts at the
          scheduled time — or start it early below.
        </CCText>

        <PrimaryButton
          label={submitting ? 'Scheduling…' : 'Schedule tournament'}
          disabled={!canCreate}
          onPress={onCreate}
        />
      </Card>

      {/* ── Scheduled list (ALL staff hosts) ────────────────────────────── */}
      <Card title="Scheduled & live tournaments">
        {(tournaments || []).length === 0 ? (
          <CCText style={styles.emptyNote}>No scheduled tournaments yet.</CCText>
        ) : (
          (tournaments || []).map((t: RoyaleTournamentSummary) => (
            <View key={t.id} style={styles.row}>
              <View style={styles.rowInfo}>
                <CCText
                  fontSize={FontSizes[13]}
                  fontWeight="semibold"
                  color={TEXT_DARK}>
                  {`${tournamentDisplayName(t)}${
                    t.is_private ? ' 🔒' : ''
                  }`}
                </CCText>
                <CCText
                  fontSize={FontSizes[11]}
                  color={CCColors.TextColor.GreyText}>
                  {`#${t.id} · ${t.difficulty} · ${t.timer_sec}s`}
                </CCText>
                <CCText
                  fontSize={FontSizes[11]}
                  color={CCColors.TextColor.GreyText}>
                  {`${t.state.toUpperCase()} · ${
                    t.player_count ?? 0
                  } joined · by ${t.created_by_name || t.created_by}`}
                </CCText>
                <CCText
                  fontSize={FontSizes[11]}
                  color={CCColors.TextColor.GreyText}>
                  {t.scheduled_at
                    ? `Starts ${new Date(t.scheduled_at).toLocaleString()}`
                    : ''}
                </CCText>
              </View>
              <View style={styles.rowActions}>
                {/* Share (copy) — the durable state-aware deep link (P8).
                    Works for scheduled/live/completed; resolves to the right
                    view by the tournament's own state. */}
                {tournamentShareText(t) ? (
                  <Pressable
                    style={[styles.smallBtn, styles.shareBtn]}
                    onPress={() => onShare(t)}>
                    <MaterialCommunityIcons
                      name={copiedId === t.id ? 'check' : 'link-variant'}
                      size={14}
                      color={TEAL_DARK}
                      style={{marginRight: 4}}
                    />
                    <CCText fontSize={FontSizes[12]} color={TEAL_DARK}>
                      {copiedId === t.id ? 'Copied link' : 'Copy link'}
                    </CCText>
                  </Pressable>
                ) : null}
                {t.state === 'scheduled' ? (
                  <>
                    <Pressable
                      style={[styles.smallBtn, styles.startBtn]}
                      onPress={() => setConfirm({kind: 'start', id: t.id})}>
                      <CCText fontSize={FontSizes[12]} color={CCColors.White}>
                        Start
                      </CCText>
                    </Pressable>
                    <Pressable
                      style={[styles.smallBtn, styles.cancelBtn]}
                      onPress={() => setConfirm({kind: 'cancel', id: t.id})}>
                      <CCText fontSize={FontSizes[12]} color={CCColors.White}>
                        Cancel
                      </CCText>
                    </Pressable>
                  </>
                ) : (
                  <View style={[styles.liveBadge]}>
                    <CCText fontSize={FontSizes[11]} color={CCColors.White}>
                      LIVE
                    </CCText>
                  </View>
                )}
              </View>
            </View>
          ))
        )}
      </Card>

      {/* ── Settings modal (shared RoyaleSettings) ──────────────────────── */}
      <SlideModal
        visible={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        modalHeaderTitle="Tournament settings"
        type="center"
        contentPadding={20}>
        {/* allowPunish={false}: punish/punishThreshold are NOT plumbed from here
            to the game service (createTournament omits them and the tournament
            start path uses the handler default `punish: false`), so the control
            promised elimination that never happened. Re-enable once it's wired
            end-to-end. */}
        <RoyaleSettings
          value={settings}
          onChange={setSettings}
          allowPunish={false}
        />
        <CCButton
          type="fill"
          stretched
          onPress={() => setSettingsOpen(false)}
          style={{marginTop: 16}}>
          Done
        </CCButton>
      </SlideModal>

      {/* ── Confirm modal (start-now / cancel) ──────────────────────────── */}
      {confirm ? (
        <View style={styles.confirmOverlay}>
          <View style={styles.confirmCard}>
            <CCText fontSize={FontSizes[15]} fontWeight="bold" color={BROWN}>
              {confirm.kind === 'start'
                ? 'Start this tournament now?'
                : 'Cancel this tournament?'}
            </CCText>
            <CCText
              fontSize={FontSizes[12]}
              color={CCColors.TextColor.GreyText}
              style={{marginTop: 2}}>
              {confirm.kind === 'start'
                ? 'Are you sure you want to start the tournament? Registered players will be entered into the race immediately.'
                : 'Registered players will be notified it was called off.'}
            </CCText>
            <View style={styles.confirmActions}>
              <Pressable
                style={[styles.smallBtn, styles.neutralBtn]}
                onPress={() => setConfirm(null)}>
                <CCText fontSize={FontSizes[12]} color={TEXT_DARK}>
                  No
                </CCText>
              </Pressable>
              <Pressable
                style={[
                  styles.smallBtn,
                  confirm.kind === 'start' ? styles.startBtn : styles.cancelBtn,
                ]}
                onPress={doConfirm}>
                <CCText fontSize={FontSizes[12]} color={CCColors.White}>
                  Yes
                </CCText>
              </Pressable>
            </View>
          </View>
        </View>
      ) : null}
    </ScrollView>
  );
}

const webInputStyle = {
  padding: 10,
  borderRadius: 10,
  border: `1px solid ${BORDER}`,
  fontSize: 14,
  color: TEXT_DARK,
  backgroundColor: '#FFFFFF',
};

const styles = StyleSheet.create({
  root: {flex: 1},
  content: {padding: vs(4), gap: vs(12)},
  card: {
    backgroundColor: CCColors.White,
    borderRadius: 14,
    padding: vs(16),
    gap: vs(10),
    borderWidth: 1,
    borderColor: BORDER,
  },
  cardTitle: {
    fontSize: FontSizes[12],
    fontWeight: '700',
    letterSpacing: 0.5,
    textTransform: 'uppercase',
    color: BROWN,
    marginBottom: vs(2),
  },
  fieldLabel: {
    fontSize: FontSizes[13],
    color: TEXT_DARK,
    marginTop: vs(4),
  },
  chipRow: {flexDirection: 'row', flexWrap: 'wrap', gap: 8},
  privateRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
    marginTop: vs(4),
  },
  settingsCard: {
    borderRadius: 12,
    borderWidth: 1,
    borderColor: BORDER,
    backgroundColor: TEAL_TINT_SOFT,
    padding: vs(12),
    gap: vs(8),
    marginTop: vs(4),
  },
  settingsCardPressed: {borderColor: TEAL},
  settingsChips: {flexDirection: 'row', gap: 8, flexWrap: 'wrap'},
  settingsHint: {fontSize: FontSizes[12], color: TEAL_DARK, fontWeight: '600'},
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: BORDER,
    backgroundColor: CCColors.Primary.lightBrown,
  },
  chipActive: {backgroundColor: TEAL, borderColor: TEAL},
  chipText: {
    fontSize: FontSizes[13],
    color: TEAL_DARK,
    textTransform: 'capitalize',
  },
  chipTextRaw: {textTransform: 'none'},
  chipTextActive: {color: CCColors.White, fontWeight: '600'},
  createNote: {
    fontSize: FontSizes[12],
    color: CCColors.TextColor.GreyText,
    marginTop: vs(2),
  },
  emptyNote: {fontSize: FontSizes[12], color: CCColors.TextColor.GreyText},
  primaryBtn: {
    backgroundColor: CCColors.Primary.Action,
    borderRadius: 12,
    paddingVertical: 14,
    alignItems: 'center',
    marginTop: vs(4),
  },
  primaryBtnDisabled: {opacity: 0.5},
  primaryBtnText: {fontSize: FontSizes[15], fontWeight: '700', color: BROWN},
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: vs(10),
    borderTopWidth: 1,
    borderTopColor: BORDER,
    gap: 8,
  },
  rowInfo: {flex: 1, gap: 2},
  rowActions: {flexDirection: 'row', gap: 8, flexWrap: 'wrap', alignItems: 'center'},
  smallBtn: {
    flexDirection: 'row',
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  shareBtn: {
    backgroundColor: TEAL_TINT_SOFT,
    borderWidth: 1,
    borderColor: TEAL,
  },
  startBtn: {backgroundColor: TEAL},
  cancelBtn: {backgroundColor: CCColors.WrongMoves?.Background || '#EB5757'},
  neutralBtn: {backgroundColor: CCColors.Primary.lightBrown},
  liveBadge: {
    backgroundColor: TEAL,
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 12,
  },
  confirmOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.35)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 20,
  },
  confirmCard: {
    backgroundColor: CCColors.White,
    borderRadius: 12,
    padding: 18,
    gap: 10,
    width: '100%',
    maxWidth: 360,
  },
  confirmActions: {flexDirection: 'row', justifyContent: 'flex-end', gap: 10},
});
